import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/theme.css';
import { ToastProvider } from './context/ToastContext';
import AppLayout from './components/layout/AppLayout';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import CommissionRules from './pages/CommissionRules';
import Clients from './pages/Clients';
import Sales from './pages/Sales';
import Reports from './pages/Reports';
import Payments from './pages/Payments';
import Settings from './pages/Settings';
import Users from './pages/Users';
import RefundPolicies from './pages/RefundPolicies';
import Login from './pages/Login';
import Register from './pages/Register';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { ThemeProvider } from './context/ThemeContext';

// Portal pages
import PortalLogin from './pages/portal/PortalLogin';
import PortalSetupPassword from './pages/portal/PortalSetupPassword';
import PortalForgotPassword from './pages/portal/PortalForgotPassword';
import PortalResetPassword from './pages/portal/PortalResetPassword';
import PortalApp from './pages/portal/PortalApp';

// ─── Admin App (requires staff auth) ───────────────────────
const AdminApp: React.FC<{ user: any; onLogin: (u: any) => void; onLogout: () => void }> = ({ user, onLogin, onLogout }) => {
  const [authView, setAuthView] = useState<'login' | 'register'>('login');

  if (!user) {
    return (
      <>
        {authView === 'login' ? (
          <Login onLogin={onLogin} onSwitchToRegister={() => setAuthView('register')} />
        ) : (
          <Register onRegister={onLogin} onSwitchToLogin={() => setAuthView('login')} />
        )}
      </>
    );
  }

  return (
    <AuthProvider user={user} logout={onLogout}>
      <SidebarProvider>
        <AppLayout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/rules" element={<CommissionRules />} />
            <Route path="/users" element={<Users />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/refund-policies" element={<RefundPolicies />} />
          </Routes>
        </AppLayout>
      </SidebarProvider>
    </AuthProvider>
  );
};

// ─── Portal Routes (separate client auth) ──────────────────
const PortalRoutes: React.FC = () => {
  const [clientUser, setClientUser] = useState<any>(() => {
    const saved = localStorage.getItem('vop_client_user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleClientLogin = useCallback((u: any) => {
    setClientUser(u);
    localStorage.setItem('vop_client_user', JSON.stringify(u));
  }, []);

  const handleClientLogout = useCallback(() => {
    setClientUser(null);
    localStorage.removeItem('vop_client_user');
  }, []);

  return (
    <Routes>
      {/* Public portal pages */}
      <Route path="/login" element={<PortalLogin onLogin={handleClientLogin} />} />
      <Route path="/setup-password" element={<PortalSetupPassword />} />
      <Route path="/forgot-password" element={<PortalForgotPassword />} />
      <Route path="/reset-password" element={<PortalResetPassword />} />

      {/* Protected portal pages */}
      <Route path="/*" element={
        clientUser
          ? <PortalApp user={clientUser} onLogout={handleClientLogout} />
          : <Navigate to="/portal/login" replace />
      } />
    </Routes>
  );
};

// ─── Root App ──────────────────────────────────────────────
function App() {
  const [user, setUser] = useState<any>(() => {
    const saved = localStorage.getItem('vop_user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = useCallback((u: any) => {
    setUser(u);
    localStorage.setItem('vop_user', JSON.stringify(u));
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('vop_user');
  }, []);

  return (
    <ThemeProvider>
    <Router>
      <ToastProvider>
        <Routes>
          {/* Portal routes — separate auth, no admin layout */}
          <Route path="/portal/*" element={<PortalRoutes />} />

          {/* Admin routes — staff auth + admin layout */}
          <Route path="/*" element={
            <AdminApp user={user} onLogin={handleLogin} onLogout={handleLogout} />
          } />
        </Routes>
      </ToastProvider>
    </Router>
    </ThemeProvider>
  );
}

export default App;
